import ApiBase from './base/ApiBase';
import { ProcessType } from '../models/dtos';
import { ApiResponseContent } from '../models/custom-models';

class AiTemplateClient extends ApiBase {
    private controllerName: string = 'AiTemplate';

    async GeneratePositionDescription(request: AiPositionDescriptionRequest, signal?: AbortSignal): Promise<AiPositionDescriptionResponse> {
        return this.PostAsync(`${this.controllerName}/Description`, request, signal);
    }

    async GetPositionDescriptionConversation(positionId: string, signal?: AbortSignal): Promise<AiPositionDescriptionConversation> {
        return this.GetAsync(`${this.controllerName}/Description/${positionId}/Conversation`, signal);
    }

    async GenerateTemplatePreview(request: AiTemplatePreviewRequest, signal?: AbortSignal): Promise<AiTemplatePreview> {
        return this.PostAsync(`${this.controllerName}/Preview`, request, signal);
    }

    async ContinueContextualChat(request: AiContextualAiRequest, signal?: AbortSignal): Promise<AiContextualAiResponse> {
        return this.PostAsync(`${this.controllerName}/ContextualChat`, request, signal);
    }

    async ApproveTemplate(request: AiTemplateApproveRequest, signal?: AbortSignal): Promise<ApiResponseContent<string>> {
        return this.PostAsync(`${this.controllerName}/Approve`, request, signal);
    }

    async RecordFeedback(request: AiTemplateFeedbackRequest, signal?: AbortSignal): Promise<void> {
        return this.PostAsync(`${this.controllerName}/Feedback`, request, signal);
    }
}

export interface AiChatMessage {
    role: 'system' | 'user' | 'assistant';
    content: string;
}

export interface AiTemplateItem {
    Description: string;
    Type: string;
    Weight: number;
    IsNegative: boolean;
    ReusesExistingProficiency: boolean;
    ExistingProficiencyId?: string | null;
    /** Client-only identity for a proficiency already copied into a template revision. */
    TemplateProficiencyId?: string | null;
}

export interface AiTemplateGroup {
    ProficiencyGroup: string;
    Weight: number;
    Proficiencies: AiTemplateItem[];
}

export interface AiTemplatePreview {
    PositionId: string;
    TemplateName: string;
    ProcessType: ProcessType;
    JobDescription: string;
    ProficiencyGroups: AiTemplateGroup[];
    AssistantMessage?: string;
    InteractionId: string;
}

export interface AiTemplatePreviewRequest {
    PositionId: string;
    PositionTitle: string;
    TemplateName: string;
    ProcessType: ProcessType;
    JobDescription: string;
    Messages?: AiChatMessage[];
    ExistingPreview?: AiTemplatePreview | null;
    Regenerate?: boolean;
    InteractionId?: string;
    MaxProficienciesPerGroup?: number;
    RoleContextSummary?: string;
}

export type AiContextualAiWorkflow = 'PositionDescription' | 'TemplateGeneration';
export type AiContextualAiStage = 'Discover' | 'Confirm' | 'Generate' | 'ReviewCoverage';
export type AiContextualAiState = 'AskQuestion' | 'ReadyToGenerate' | 'Generated' | 'NeedsCoverageDecision';
export type AiContextualAiGap = 'RoleOutcomesAndFailureCosts' | 'TechnicalDomainAbilities' | 'DecisionAuthority' | 'HighImpactMistakes' | 'PositiveBehaviors' | 'CollaborationAudiences' | 'SafetyComplianceEthics' | 'PerformanceDistinction';
export type AiContextualAiResultKind = 'None' | 'PositionDescription' | 'TemplatePreview';

export interface AiContextualAiAnswer { GapKey: AiContextualAiGap; Answer: string; }
export type AiTemplateCoverageAction = 'AddSuggestedItems' | 'DescribeMissingCoverage' | 'RegenerateCategory' | 'ConfirmSufficient';
export interface AiTemplateCoverageConfirmation { ProficiencyGroup: string; CoverageFingerprint: string; ConfirmedSufficient: boolean; ContextSummary: string; }
export interface AiContextualAiRequest {
    Workflow: AiContextualAiWorkflow;
    Stage: AiContextualAiStage;
    PositionId?: string;
    PositionTitle: string;
    JobDescription: string;
    Answers?: AiContextualAiAnswer[];
    Messages?: AiChatMessage[];
    ContextSummary?: string;
    GenerateNow?: boolean;
    InteractionId?: string;
    TemplateName?: string;
    ProcessType?: ProcessType;
    ExistingPreview?: AiTemplatePreview | null;
    Regenerate?: boolean;
    MaxProficienciesPerGroup?: number;
    SuggestionDecisions?: AiTemplateSuggestionDecision[];
    CoverageConfirmations?: AiTemplateCoverageConfirmation[];
    CoverageContractVersion?: number;
    ChangedCoverageGroups?: string[];
}
export interface AiContextualCoverageDecisionWire {
    ProficiencyGroup: string; IncludedCount: number; RecommendedMinimum: number; CoverageFingerprint: string; QuestionText: string;
    SuggestedAdditions: AiTemplateItem[]; AvailableActions: AiTemplateCoverageAction[]; CoverageContractVersion: number;
}
export interface AiContextualAiResponse {
    Workflow: AiContextualAiWorkflow; State: AiContextualAiState; ContextSummary: string; QuestionText?: string; Rationale?: string;
    GapKey?: AiContextualAiGap; SuggestedChoices: string[]; AllowFreeText: boolean; AllowSkip: boolean; AllowGenerateNow: boolean;
    ResultKind: AiContextualAiResultKind; PositionDescription?: AiPositionDescriptionResponse; TemplatePreview?: AiTemplatePreview;
    InteractionId: string; CoverageDecision?: AiContextualCoverageDecisionWire;
}

export interface AiTemplateApproveRequest {
    PositionId: string;
    TemplateName: string;
    ProcessType: ProcessType;
    JobDescription: string;
    Preview: AiTemplatePreview;
    InteractionId?: string;
    Rating?: number | null;
    Feedback?: string;
    SuggestionDecisions?: AiTemplateSuggestionDecision[];
    CoverageConfirmations?: AiTemplateCoverageConfirmation[];
    CoverageContractVersion?: number;
    ChangedCoverageGroups?: string[];
}

export interface AiTemplateSuggestionDecision {
    ProficiencyGroup: string;
    Description: string;
    Type: string;
    IsNegative: boolean;
    Decision: 'denied';
}

export interface AiPositionDescriptionRequest {
    PositionTitle: string;
    CurrentDescription: string;
    Messages?: AiChatMessage[];
    PositionId?: string;
    InteractionId?: string;
    RoleContextSummary?: string;
    ContextAnswers?: AiContextualAiAnswer[];
}

export interface AiPositionDescriptionResponse {
    UpdatedDescription: string;
    AssistantMessage: string;
    InteractionId: string;
    ContextSummary: string;
}

export interface AiPositionDescriptionConversation {
    InteractionId: string;
    Messages: AiChatMessage[];
    ContextSummary: string;
}

export type AiFeedbackOutcome = 'Accepted' | 'Dismissed' | 'Rejected';

export interface AiTemplateFeedbackRequest {
    InteractionId: string;
    PositionId?: string;
    Workflow: 'PositionDescription' | 'TemplatePreview';
    Outcome: AiFeedbackOutcome;
    Rating?: number | null;
    Feedback?: string;
    FinalPositionDescription?: string;
    FinalPreview?: AiTemplatePreview;
    SuggestionDecisions?: AiTemplateSuggestionDecision[];
}

export default AiTemplateClient;
